import 'react-multi-carousel/lib/styles.css';

import { Box, IconButton, alpha, styled, useTheme } from '@mui/material';
import type { ReactElement } from 'react';
import type { Theme } from '@mui/material/styles';
import type { ArrowProps } from 'react-multi-carousel/lib/types';
import Carousel from 'react-multi-carousel';
import KeyboardArrowLeftIcon from '@mui/icons-material/KeyboardArrowLeft';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';

export type GalleryProps = {
  children: ReactElement[];
  translatedText: {
    galleryRightArrowTitle: string;
    galleryLeftArrowTitle: string;
  };
};

interface CustomArrowProps extends ArrowProps {
  title: string;
}

const CustomSlideButton = styled(IconButton)<{ direction: 'left' | 'right' }>(
  ({ theme, direction }: { theme: Theme; direction: 'left' | 'right' }) => ({
    position: 'absolute',
    outline: 0,
    transition: 'all .5s',
    borderRadius: 0,
    zIndex: 1000,
    border: 0,
    background: theme.palette.common.white,
    width: '44px',
    height: '36px',
    opacity: 1,
    cursor: 'pointer',
    [direction]: 0,
    '&:hover': {
      background: alpha(theme.palette.common.white, 0.4),
    },
    '&:focus': {
      background: alpha(theme.palette.common.white, 0.9),
      border: `2px solid ${theme.palette.common.black}`,
      borderRadius: '4px',
    },
  })
);

const CarouselContainer = styled(Box)(() => ({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  position: 'relative',
  '&:before': {
    content: '""',
    position: 'absolute',
    zIndex: 1,
    bottom: 0,
    pointerEvents: 'none',
    width: '130px',
    height: '100%',
    right: 0,
  },
  '&:after': {
    content: '""',
    position: 'absolute',
    zIndex: 1,
    bottom: 0,
    pointerEvents: 'none',
    width: '130px',
    height: '100%',
    left: 0,
  },

  '& .carousel-container': {
    height: '400px',
    '& .react-multi-carousel-track  ': {
      height: '100%',
    },
  },
}));

const CarouselItem = styled(Box)(() => ({
  position: 'relative',
  height: '100%',
  width: '95%',
  display: 'flex',
  justifyContent: 'flex-end',
  flexDirection: 'column',
}));

const CustomRightArrow = ({ onClick, title }: CustomArrowProps) => {
  const theme = useTheme();
  return (
    <CustomSlideButton
      onClick={onClick}
      aria-label={title}
      direction="right"
      disableRipple
      data-testid="gallery-arrow-forward"
      title={title}
      theme={theme}
    >
      <KeyboardArrowRightIcon />
    </CustomSlideButton>
  );
};

const CustomLeftArrow = ({ onClick, title }: CustomArrowProps) => {
  const theme = useTheme();
  return (
    <CustomSlideButton
      onClick={onClick}
      aria-label={title}
      direction="left"
      disableRipple
      data-testid="gallery-arrow-back"
      title={title}
      theme={theme}
    >
      <KeyboardArrowLeftIcon />
    </CustomSlideButton>
  );
};

/**
 * Gallery component from [MicroLibraryAppDetailsUI](https://dev.azure.com/orcha/MicroLibraryTemplate/_git/MicroLibraryAppDetailsUI)
 * @param {GalleryProps} galleryProps - The props for the Gallery component
 * @returns {ReactElement} The Gallery component
 * @param translatedText is required, this is the text that as already been translated by the consumer of this component.
 */

export const Gallery = (galleryProps: GalleryProps) => {
  const { children, translatedText } = galleryProps;
  const theme = useTheme();
  const getSlidesCount = (maxSlidesCount: number) => {
    return children && children?.length >= maxSlidesCount
      ? maxSlidesCount
      : children?.length || 0;
  };

  const responsive = {
    xxl: {
      breakpoint: { max: 10000, min: theme.breakpoints.values.xl },
      items: getSlidesCount(5),
      slidesToSlide: getSlidesCount(5),
    },
    xl: {
      breakpoint: {
        max: theme.breakpoints.values.xl,
        min: theme.breakpoints.values.lg,
      },
      items: getSlidesCount(4),
      slidesToSlide: getSlidesCount(4),
    },
    lg: {
      breakpoint: {
        max: theme.breakpoints.values.lg,
        min: theme.breakpoints.values.md,
      },
      items: getSlidesCount(3),
      slidesToSlide: getSlidesCount(3),
    },
    md: {
      breakpoint: {
        max: theme.breakpoints.values.md,
        min: theme.breakpoints.values.sm,
      },
      items: getSlidesCount(2),
      slidesToSlide: getSlidesCount(2),
    },
    sm: {
      breakpoint: { max: theme.breakpoints.values.sm, min: 0 },
      items: 1,
      slidesToSlide: 1,
    },
  };

  return (
    <CarouselContainer data-testid="gallery-container">
      <Carousel
        responsive={responsive}
        ssr
        keyBoardControl
        containerClass="carousel-container"
        infinite
        centerMode
        customRightArrow={
          <CustomRightArrow
            title={translatedText?.galleryRightArrowTitle || ''}
          />
        }
        customLeftArrow={
          <CustomLeftArrow
            title={translatedText?.galleryLeftArrowTitle || ''}
          />
        }
        arrows
      >
        {children?.map((item) => {
          return <CarouselItem key={item.key}>{item}</CarouselItem>;
        })}
      </Carousel>
    </CarouselContainer>
  );
};
